/* eslint-disable react/no-danger */
import { getPagePath, trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { DSIcon } from '@spotahome/ui-library/base';

import { BaseChip, Button, InfoBanner } from '@spotahome/ui-library';

import { Mediator } from '@spotahome/soyuz-mediator';
import {
  OPEN_GUARANTEE_MODAL,
  OPEN_VERIFICATION_MODAL,
  OPEN_DEPOSIT_PROTECTION_MODAL
} from '@spotahome/ui-library/src/tenant/components/utils/constants';
import ValuePropositionTags from '@spotahome/ui-library/src/tenant/components/ValuePropositionTags/ValuePropositionTags';
import SpotahomeGuaranteeCircledIcon from '@spotahome/ui-library/src/tenant/components/icons/SpotahomeGuaranteeCircledIcon';
import CheckPropertyEyeCircledIcon from '@spotahome/ui-library/src/tenant/components/icons/CheckPropertyEyeCircledIcon';
import PiggyBankLabelCircledIcon from '@spotahome/ui-library/src/tenant/components/icons/PiggyBankLabelCircledIcon';
import LockIcon from '@spotahome/ui-library/src/tenant/components/icons/LockIcon';

import { usePricingContext } from '@spotahome/marketplace-common/src/context/PricingContext';

import { ListingSectionObservable } from '../ListingSection';
import ListingTitle from '../ListingTitle';

import './CancellationPoliciesRedesign.scss';

const TAG_MODAL_DICTIONARY = {
  spotahomeGuarantee: {
    modal: OPEN_GUARANTEE_MODAL,
    event: 'spotahomeGuarantee',
    title: () => trans('amp.property.sah_check.guarantee.title'),
    body: () => trans('amp.property.sah_check.guarantee.content')
  },
  checkProperty24h: {
    modal: OPEN_GUARANTEE_MODAL,
    event: 'checkProperty24h',
    title: () => trans('amp.property.sah_check.24_hours.title'),
    body: () => trans('amp.property.sah_check.24_hours.content')
  },
  noDeposit: {
    modal: OPEN_DEPOSIT_PROTECTION_MODAL,
    event: 'noDeposit'
  },
  depositProtected: {
    modal: OPEN_DEPOSIT_PROTECTION_MODAL,
    event: 'depositProtected'
  },
  verifiedLandlord: {
    modal: OPEN_VERIFICATION_MODAL,
    event: 'verifiedLandlord'
  }
};

const CancellationPoliciesRedesign = () => {
  const pricingContext = usePricingContext();

  const handleClickSeeMore = () => {
    SoyuzAnalytics.sendGA4Event('CancellationPoliciesRedesign', {
      action: 'SeeMore'
    });
  };

  const handleClickTag = tagId => {
    const modalData = TAG_MODAL_DICTIONARY[tagId];

    if (!modalData) {
      return;
    }

    SoyuzAnalytics.sendGA4Event('CancellationPoliciesRedesign', {
      action: modalData.event
    });

    if (modalData.modal === OPEN_GUARANTEE_MODAL) {
      Mediator.publish(modalData.modal, {
        title: modalData.title(),
        body: modalData.body()
      });
    } else {
      Mediator.publish(modalData.modal);
    }
  };

  return (
    <ListingSectionObservable section="cancellationPolicies">
      <ListingTitle role="secondary">
        {trans('cancellation.policies.title')}
      </ListingTitle>
      <div className="cancellation-policies-redesign__section">
        <h3 className="cancellation-policies-redesign__section-title">
          {trans('cancellation.policies.before.landlord.accepts')}
        </h3>
        <p className="cancellation-policies-redesign__section-content cancellation-policies-redesign__section-content--inline">
          <DSIcon
            name="check_circle_transparent"
            className="cancellation-policies-container__verified-icon"
          />{' '}
          {trans('cancellation.policies.nothing.will.be.charged')}
        </p>
      </div>
      <div className="cancellation-policies-redesign__section">
        <h3 className="cancellation-policies-redesign__section-title">
          {trans('cancellation.policies.after.landlord.accepts')}
        </h3>
        <ul className="cancellation-policies-redesign__section-content">
          <li
            dangerouslySetInnerHTML={{
              __html: trans('cancellation.policies.60.days.before.bold')
            }}
          />
          <li
            dangerouslySetInnerHTML={{
              __html: trans('cancellation.policies.30.days.before.bold')
            }}
          />
          <li
            dangerouslySetInnerHTML={{
              __html: trans('cancellation.policies.on.move.in.bold')
            }}
          />
        </ul>
      </div>
      <InfoBanner
        closable={false}
        theme="suggestion-light"
        className="cancellation-policies-redesign__section"
        contentClassName="cancellation-policies-redesign__info-banner"
        content={
          <span
            dangerouslySetInnerHTML={{
              __html: trans('cancellation.policies.fee.warning.bold')
            }}
          />
        }
        icon={<DSIcon name="warning" />}
      />
      <ValuePropositionTags
        className="cancellation-policies-redesign__section"
        titleClassName="cancellation-policies-redesign__section-title"
        title={trans('cancellation.policies.value.proposition.remember.title')}
        securityDepositType={pricingContext?.securityDepositType}
        isInstantBooking={false}
        bills={[]}
        plan=""
        allowPermanentContract={false}
        verifiedLandlord
        onClickTag={handleClickTag}
      />
      <Button
        onClick={handleClickSeeMore}
        className="cancellation-policies-redesign__link"
        color="link"
        href={getPagePath('help-center.cancellation-policies')}
        target="_blank"
      >
        {trans('cancellation.policies.read.more')}
      </Button>
    </ListingSectionObservable>
  );
};

export default CancellationPoliciesRedesign;
